import { useId } from 'react'
import { useProducts } from '../context/ProductsContext'

/**
 * Photorealistic SVG pharmaceutical cream tube.
 * Same label system as PeptideVial: LooksLab logo + compound name + concentration.
 */
export default function CreamTube({
  formulaName = 'TRETINOIN',
  formulaSubtitle = '',
  concentration = '0.05%',
  labelSize,
  wrapClassName = 'peptide-vial-float',
  rootClassName = '',
}) {
  const uid = useId().replace(/:/g, '')
  const g = (n) => `${n}-${uid}`
  const { branding } = useProducts()
  const logoSrc = branding?.logoDark?.url ?? '/logos/lookslab-logo.png'

  const W = 110, H = 220

  // Cap (screw-on, at top)
  const CAP_X = 36, CAP_W = 38, CAP_Y = 2, CAP_H = 26
  // Shoulder (cap → body taper)
  const SH_TOP_X = 36, SH_TOP_W = 38, SH_Y = 28, SH_H = 16
  const SH_BOT_X = 16, SH_BOT_W = 78
  // Body (main tube)
  const BDY_X = 16, BDY_W = 78, BDY_Y = 44, BDY_H = 128
  // Crimp (bottom fold)
  const CRP_Y = BDY_Y + BDY_H  // 172
  const CRP_H = 28

  const MID = W / 2
  const GX1 = BDY_X, GX2 = BDY_X + BDY_W

  // Label geometry (on body)
  const LBL_X = BDY_X + 4
  const LBL_W = BDY_W - 8
  const LBL_Y = BDY_Y + 8
  const LBL_H = BDY_H - 24
  const LMX = LBL_X + LBL_W / 2

  // Scale name
  const nameDisplay = formulaName.toUpperCase()
  const nameFs = nameDisplay.length <= 5 ? 15
    : nameDisplay.length <= 8 ? 12
    : nameDisplay.length <= 12 ? 9
    : 7
  const useTextLength = nameDisplay.length > 9

  // 3-zone layout (same as vial)
  const Z1_H_LOGO = 18
  const Z2_H = nameFs + 10
  const Z3_BADGE_H = 10
  const CONTENT_H = Z2_H + 13 + 8 + 6
  const FREE = LBL_H - Z1_H_LOGO - CONTENT_H - Z3_BADGE_H
  const PAD = Math.max(2, Math.round(FREE / 2))
  const Z1_H = Z1_H_LOGO + PAD
  const Z1_Y = LBL_Y
  const Z2_Y = LBL_Y + Z1_H
  const Z3_Y = Z2_Y + Z2_H
  const Z3_H = LBL_H - Z1_H - Z2_H
  const Z2_TEXT_Y = Z2_Y + 5 + Math.round(nameFs * 0.74)
  const Z3_BOX_TOP = PAD
  const Z3_BOX_H = 12
  const Z3_LYO_Y = Z3_BOX_TOP + Z3_BOX_H + 6
  const Z3_BADGE_TOP = Z3_H - Z3_BADGE_H

  const crimpPath = [
    `M ${BDY_X} ${CRP_Y}`,
    `L ${BDY_X + BDY_W} ${CRP_Y}`,
    `L ${BDY_X + BDY_W - 10} ${CRP_Y + CRP_H * 0.42}`,
    `L ${BDY_X + BDY_W - 6} ${CRP_Y + CRP_H * 0.60}`,
    `L ${BDY_X + BDY_W - 14} ${CRP_Y + CRP_H * 0.80}`,
    `L ${BDY_X + BDY_W - 10} ${CRP_Y + CRP_H}`,
    `L ${BDY_X + 10} ${CRP_Y + CRP_H}`,
    `L ${BDY_X + 14} ${CRP_Y + CRP_H * 0.80}`,
    `L ${BDY_X + 6} ${CRP_Y + CRP_H * 0.60}`,
    `L ${BDY_X + 10} ${CRP_Y + CRP_H * 0.42}`,
    'Z',
  ].join(' ')

  const shPath = [
    `M ${SH_TOP_X} ${SH_Y}`,
    `L ${SH_TOP_X + SH_TOP_W} ${SH_Y}`,
    `L ${SH_BOT_X + SH_BOT_W} ${SH_Y + SH_H}`,
    `L ${SH_BOT_X} ${SH_Y + SH_H}`,
    'Z',
  ].join(' ')

  return (
    <div className={rootClassName || undefined}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      <svg
        className={wrapClassName}
        viewBox={`0 0 ${W} ${H}`}
        width={W} height={H}
        style={{ display: 'block', maxWidth: '100%', overflow: 'visible' }}
        aria-label={`${formulaName} ${concentration}`}
        role="img"
      >
        <defs>
          <linearGradient id={g('tube')} x1={GX1} y1="0" x2={GX2} y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="rgba(160,160,160,0.95)" />
            <stop offset="5%"   stopColor="rgba(200,200,200,0.70)" />
            <stop offset="12%"  stopColor="rgba(240,240,240,0.30)" />
            <stop offset="20%"  stopColor="rgba(255,255,255,0.55)" />
            <stop offset="32%"  stopColor="rgba(248,248,248,0.15)" />
            <stop offset="50%"  stopColor="rgba(245,245,245,0.08)" />
            <stop offset="68%"  stopColor="rgba(248,248,248,0.10)" />
            <stop offset="80%"  stopColor="rgba(255,255,255,0.18)" />
            <stop offset="90%"  stopColor="rgba(190,190,190,0.40)" />
            <stop offset="100%" stopColor="rgba(130,130,130,0.90)" />
          </linearGradient>
          <radialGradient id={g('tubeglow')} cx="38%" cy="40%" r="45%">
            <stop offset="0%"   stopColor="rgba(255,255,255,0.12)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <linearGradient id={g('tubefill')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#f0f0ee" />
            <stop offset="100%" stopColor="#e8e8e6" />
          </linearGradient>
          <linearGradient id={g('crimp')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#d0d0ce" />
            <stop offset="30%"  stopColor="#b8b8b6" />
            <stop offset="55%"  stopColor="#c8c8c6" />
            <stop offset="75%"  stopColor="#a8a8a6" />
            <stop offset="100%" stopColor="#989896" />
          </linearGradient>
          <linearGradient id={g('cap')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#f0f0ee" />
            <stop offset="40%"  stopColor="#d8d8d6" />
            <stop offset="100%" stopColor="#b8b8b6" />
          </linearGradient>
          <linearGradient id={g('capx')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.10)" />
            <stop offset="20%"  stopColor="rgba(255,255,255,0.18)" />
            <stop offset="50%"  stopColor="rgba(255,255,255,0.04)" />
            <stop offset="80%"  stopColor="rgba(255,255,255,0.12)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.12)" />
          </linearGradient>
          <linearGradient id={g('cylshadow')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.72)" />
            <stop offset="8%"   stopColor="rgba(0,0,0,0.48)" />
            <stop offset="16%"  stopColor="rgba(0,0,0,0.18)" />
            <stop offset="26%"  stopColor="rgba(0,0,0,0.03)" />
            <stop offset="36%"  stopColor="rgba(0,0,0,0)" />
            <stop offset="64%"  stopColor="rgba(0,0,0,0)" />
            <stop offset="76%"  stopColor="rgba(0,0,0,0.04)" />
            <stop offset="86%"  stopColor="rgba(0,0,0,0.20)" />
            <stop offset="94%"  stopColor="rgba(0,0,0,0.46)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.70)" />
          </linearGradient>
          <linearGradient id={g('cylspec')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
            <stop offset="16%"  stopColor="rgba(255,255,255,0.06)" />
            <stop offset="22%"  stopColor="rgba(255,255,255,0.20)" />
            <stop offset="28%"  stopColor="rgba(255,255,255,0.05)" />
            <stop offset="38%"  stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          <radialGradient id={g('lblrad')} cx="42%" cy="50%" r="55%">
            <stop offset="0%"   stopColor="rgba(255,255,255,0.16)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <linearGradient id={g('topedge')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.18)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </linearGradient>
          <linearGradient id={g('botedge')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.16)" />
          </linearGradient>
          <clipPath id={g('lblclip')}>
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} rx="1.5" />
          </clipPath>
          <clipPath id={g('bclip')}>
            <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} />
          </clipPath>
          <radialGradient id={g('shadow')} cx="50%" cy="10%" rx="50%" ry="100%">
            <stop offset="0%"   stopColor="rgba(0,0,0,0.45)" />
            <stop offset="50%"  stopColor="rgba(0,0,0,0.15)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <pattern id={g('ridge')} x="0" y="0" width="2.2" height="1" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="1.0" height={CAP_H} fill="rgba(0,0,0,0.07)" />
          </pattern>
        </defs>

        <ellipse cx={MID} cy={CRP_Y + CRP_H + 8} rx={38} ry={6} fill={`url(#${g('shadow')})`} />

        <path d={crimpPath} fill={`url(#${g('crimp')})`}
          stroke="rgba(90,90,88,0.50)" strokeWidth="0.5" strokeLinejoin="round" />
        <line x1={BDY_X + 10} y1={CRP_Y + CRP_H * 0.42} x2={BDY_X + BDY_W - 10} y2={CRP_Y + CRP_H * 0.42}
          stroke="rgba(80,80,78,0.35)" strokeWidth="0.6" />
        <line x1={BDY_X + 6} y1={CRP_Y + CRP_H * 0.60} x2={BDY_X + BDY_W - 6} y2={CRP_Y + CRP_H * 0.60}
          stroke="rgba(80,80,78,0.25)" strokeWidth="0.5" />
        <line x1={BDY_X + 14} y1={CRP_Y + CRP_H * 0.80} x2={BDY_X + BDY_W - 14} y2={CRP_Y + CRP_H * 0.80}
          stroke="rgba(80,80,78,0.20)" strokeWidth="0.5" />

        <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} fill={`url(#${g('tubefill')})`} />
        <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} fill={`url(#${g('tube')})`} />
        <rect x={BDY_X} y={BDY_Y} width={BDY_W} height={BDY_H} fill={`url(#${g('tubeglow')})`} />
        <line x1={BDY_X + 0.5} y1={BDY_Y} x2={BDY_X + 0.5} y2={BDY_Y + BDY_H}
          stroke="rgba(200,200,200,0.55)" strokeWidth="0.6" />
        <line x1={BDY_X + BDY_W - 0.5} y1={BDY_Y} x2={BDY_X + BDY_W - 0.5} y2={BDY_Y + BDY_H}
          stroke="rgba(100,100,100,0.45)" strokeWidth="0.6" />

        <g clipPath={`url(#${g('bclip')})`}>
          <g clipPath={`url(#${g('lblclip')})`}>
            <rect x={LBL_X} y={Z1_Y} width={LBL_W} height={Z1_H} fill="#f5f4f1" />
            <rect x={LBL_X} y={Z1_Y} width={LBL_W} height={Z1_H} fill={`url(#${g('lblrad')})`} />
            <image href={logoSrc}
              x={LBL_X} y={Z1_Y + (Z1_H - Z1_H_LOGO) / 2}
              width={LBL_W} height={Z1_H_LOGO}
              preserveAspectRatio="xMidYMid meet" />
            <rect x={LBL_X} y={Z2_Y} width={LBL_W} height={Z2_H} fill="#111111" />
            <text x={LMX} y={Z2_TEXT_Y} textAnchor="middle"
              fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize={nameFs} fontWeight="900" letterSpacing="0.01em" fill="#ffffff"
              {...(useTextLength ? { textLength: LBL_W - 8, lengthAdjust: 'spacingAndGlyphs' } : {})}>
              {nameDisplay}
            </text>
            <rect x={LBL_X} y={Z3_Y} width={LBL_W} height={Z3_H} fill="#f5f4f1" />
            <rect x={LBL_X} y={Z3_Y} width={LBL_W} height={Z3_H} fill={`url(#${g('lblrad')})`} />
            <rect x={LBL_X + 7} y={Z3_Y + Z3_BOX_TOP} width={44} height={Z3_BOX_H}
              fill="none" stroke="#1a1a1a" strokeWidth="1.0" rx="0.5" />
            <text x={LBL_X + 29} y={Z3_Y + Z3_BOX_TOP + Math.round(Z3_BOX_H * 0.72)}
              textAnchor="middle" fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize="8.5" fontWeight="800" letterSpacing="0.01em" fill="#111111">
              {(labelSize ?? concentration).toUpperCase()}
            </text>
            <text x={LBL_X + 7} y={Z3_Y + Z3_LYO_Y} textAnchor="start"
              fontFamily="'Inter','Helvetica Neue',sans-serif" fontSize="4.8" fontWeight="600" fill="#555555"
              textLength={44} lengthAdjust="spacingAndGlyphs">TOPICAL CREAM</text>
            <rect x={LBL_X} y={Z3_Y + Z3_BADGE_TOP} width={LBL_W} height={Z3_BADGE_H} fill="#111111" />
            <text x={LMX} y={Z3_Y + Z3_BADGE_TOP + 7.2} textAnchor="middle"
              fontFamily="'Inter','Helvetica Neue',sans-serif"
              fontSize="5" fontWeight="700" letterSpacing="0.08em" fill="#efefef">
              FOR LABORATORY USE ONLY
            </text>
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} fill={`url(#${g('cylshadow')})`} />
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={LBL_H} fill={`url(#${g('cylspec')})`} />
            <rect x={LBL_X} y={LBL_Y} width={LBL_W} height={8} fill={`url(#${g('topedge')})`} />
            <rect x={LBL_X} y={LBL_Y + LBL_H - 8} width={LBL_W} height={8} fill={`url(#${g('botedge')})`} />
          </g>
        </g>

        <path d={shPath} fill={`url(#${g('tubefill')})`} />
        <path d={shPath} fill={`url(#${g('tube')})`} />
        <line x1={SH_TOP_X} y1={SH_Y} x2={SH_TOP_X + SH_TOP_W} y2={SH_Y}
          stroke="rgba(180,180,180,0.30)" strokeWidth="0.5" />
        <line x1={SH_TOP_X} y1={SH_Y} x2={SH_BOT_X} y2={SH_Y + SH_H}
          stroke="rgba(200,200,200,0.28)" strokeWidth="0.5" />
        <line x1={SH_TOP_X + SH_TOP_W} y1={SH_Y} x2={SH_BOT_X + SH_BOT_W} y2={SH_Y + SH_H}
          stroke="rgba(90,90,90,0.35)" strokeWidth="0.5" />

        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="4"
          fill={`url(#${g('cap')})`} />
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="4"
          fill={`url(#${g('ridge')})`} opacity="0.7" />
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="4"
          fill={`url(#${g('capx')})`} />
        <rect x={CAP_X + 4} y={CAP_Y + 2} width={CAP_W - 8} height={CAP_H * 0.35} rx="3"
          fill="rgba(255,255,255,0.28)" />
        <rect x={CAP_X} y={CAP_Y} width={CAP_W} height={CAP_H} rx="4"
          fill="none" stroke="rgba(80,80,78,0.50)" strokeWidth="0.6" />
        <rect x={CAP_X} y={CAP_Y + CAP_H - 5} width={CAP_W} height={5}
          fill="rgba(0,0,0,0.08)" />

      </svg>
    </div>
  )
}
